<script setup>
import { ref, shallowRef, inject } from 'vue';
import { getPrettyPrice, getCardPrice } from '@client/js/shared/utils';
import { useCustomerStore } from '@client/js/store/customer';
import useOrderStore from '@client/js/store/order';
import useCartStore from '@client/js/store/cart';
import FieldGroup from '@client/js/components/FieldGroup.vue';
import PaymentForm from '@client/js/components/PaymentForm.vue';

const paymentBtn = shallowRef(null);
const paymentResponseMessage = shallowRef('');
const paymentForm = ref(null);

const customerStore = useCustomerStore();
const orderStore = useOrderStore();
const cartStore = useCartStore();

const restApi = inject('restApi');

let paymentSuccess = ref(0);

const handlePayment = async () => {
  paymentResponseMessage.value = '';
  let address = String(customerStore.isDeliveryOrder ? `${customerStore?.street}, ${customerStore?.city}, ${customerStore?.state}, ${customerStore?.zip}` : '');
  console.debug("🧾 After-party payment");
  const requestBody = {
    'amount': Number(getCardPrice(cartStore.checkoutTotal)).toFixed(0),
    'token': 'clv_token',         // pre-request mock
    'orderId': orderStore.orderId,
    'email': customerStore.email,
    'message': '',
  };

  try {
    paymentBtn.value.setAttribute('data-processing', 'true');
    let tokenizerResult = null;
    try {
      tokenizerResult = await paymentForm.value.clover.createToken();
    } catch (error) {
      console.error('🍀 Card createToken error: %o', error);
    }
    if (!tokenizerResult?.token) {
      paymentResponseMessage.value = 'There was an issue with processing your card. Please try again.';
      console.error('🍀 Card tokenization error: %o', tokenizerResult);
      throw new Error('Card tokenization error');
    }
    requestBody.token = tokenizerResult.token;
    let paymentResponse = await restApi.post('clover/pay-for-order', requestBody);
    processResponse(paymentResponse);
  } catch (data) {
    processResponse(data);
    let debugErrorMsg = '';
    if (data.errors) {
      debugErrorMsg += Object.values(data.errors).join(', ');
    } else if (data?.message){
      debugErrorMsg += data.message;
    }
    console.error('', debugErrorMsg, data);
  } finally {
    paymentBtn.value.removeAttribute('data-processing', '');
  }
}

const processResponse = (data) => { // axios object
  let msg = '';
  let responseData = data?.data || data?.response?.data // code, message, data
  if ( data?.status ) {
    if (responseData?.message) {
      msg = responseData?.message
    } else if ( data?.message ) {
      msg = data?.message;
    }
    if (data?.status >= 200 && data?.status < 300) {
      paymentSuccess.value = 1;
      msg = msg || 'Success';
      console.log('Positive payment response', data);
    } else if (data?.status >= 400) {
      msg = msg || 'Failed';
      console.log('Negative payment response', data);
    }
    msg = msg.replaceAll('_', ' ');
    paymentResponseMessage.value = msg.charAt(0).toUpperCase() + msg.slice(1)
  } else {
    console.log('unknown');
  }

}

</script>

<template>
  <FieldGroup class="no-border">
    <template #legend v-show="!paymentSuccess">Pay For An Order</template>
    <div class="payment-container">
      <keep-alive>
        <PaymentForm ref="paymentForm" v-show="!paymentSuccess"/>
      </keep-alive>
      <Transition name="error" appear>
        <div :class="['requestResults', {'error': !paymentSuccess }, { 'success': paymentSuccess }]" v-show="paymentResponseMessage.length > 0">
          <div class="result" v-text="paymentResponseMessage"></div>
        </div>
      </Transition>
      <button
        type="button"
        role="button"
        class="btn btn-payment btn-action primary"
        v-show="!paymentSuccess"
        :disabled="!customerStore.meta.valid || paymentForm?.hasErrors"
        @click="handlePayment"
        ref="paymentBtn"
       >
          <span>Pay Now • {{ getPrettyPrice(getCardPrice(cartStore.checkoutTotal)) }}</span>
      </button>
    </div>
  </FieldGroup>
</template>

<style lang="scss" scoped>
.payment-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.5rem
}
 
.payment, 
.btn-payment,
.requestResults {
  margin: 0 auto;
}
.payment {
  margin-bottom: 0.75rem;
}
.btn-payment {
  text-align: center;
  justify-content: space-between;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  padding: 0.75rem 1.125rem;
  font-size: 0.875rem;
  font-weight: 600;
  flex: 1 1 100%;
  align-self: center;
  min-width: 33.33%;

  svg {
    margin-left: 0.625rem;
    width: 1rem;
    transform: scaleX(-1);
  }

  &:disabled {
    opacity: 0.5;
    background-color: #9b9b9b;
  }
}

.requestResults {
    width: 100%;
    padding: 0.75rem 1.5rem 0.75rem 0.75rem;
    border-radius: 8px;
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 0.75rem;

    &Title {
        font-weight: 700;
        letter-spacing: 0.25px;
        margin-bottom: 0.25rem;
    }
    .result {
        margin-bottom: 0.125rem;
    }
    
    &.error {
      background-color: #fff0f5;
      border: 2px solid #ffafbb;
    }

    &.success {
      background-color: #eeffea;
      border: 2px solid #67b850;
      text-align: center;
      font-size: 0.875rem;
      line-height: 1.4;
    }

}

.error-enter-active,
.error-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

/* вход */
.error-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
.error-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* выход (обратное) */
.error-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.error-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

</style>